//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

/**
 * A short, stable digest, for naming and matching -- never for secrecy.
 *
 * One use is keeping names out of `localStorage`, where they would otherwise
 * sit in plaintext for anyone with access to the browser profile.  That removes
 * the names, not the information: anyone holding a list of candidate names can
 * hash them and see which are present, so this keeps data from being *read*,
 * never from being *confirmed*.
 */
// 64-bit FNV-1a: a fast, well-distributed non-cryptographic hash.
// Returns a 16-character hex string.
export const fnv1a64 = (str: string): string => {
  let hash = 14695981039346656037n; // FNV offset basis.
  for (let i = 0; i < str.length; i++) {
    hash ^= BigInt(str.charCodeAt(i));
    hash = BigInt.asUintN(64, hash * 1099511628211n); // FNV prime.
  }
  return hash.toString(16).padStart(16, '0');
};

// A 31-based rolling hash, 32 bits wide, base-36 encoded.  Shorter and cheaper
// than fnv1a64 but collision-prone enough that the caller has to tolerate two
// inputs sharing a digest -- fine for namespacing a storage key per user, not
// for distinguishing values.
export const hashString = (str: string): string => {
  let h = 0;
  for (let i = 0; i < str.length; i++)
    h = Math.imul(31, h) + str.charCodeAt(i) | 0;

  return h.toString(36);
};

// Whether a keyed digest is available.  crypto.subtle exists only in a secure
// context, so a page served over plain HTTP to anything but localhost has no
// HMAC to offer and falls back to the unkeyed digest below.
export const canKeyDigests = (): boolean =>
  typeof crypto !== 'undefined' && crypto.subtle != null;

const KEYED_PREFIX = 'k:';

/**
 * HMAC-SHA-256 of `value` under `key`, truncated to 16 hex characters and
 * tagged so it can be told from an unkeyed digest.
 *
 * This is what makes a digest worth storing.  An unkeyed one is not protection:
 * names are short and few, so anyone can hash a list of candidates and see
 * which are present -- no inversion needed.  Under a key they hold, they can't.
 * So the key must never be persisted alongside the digests.
 */
export async function keyedDigest(key: string, value: string): Promise<string> {
  const encoder = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    'raw', encoder.encode(key), { name: 'HMAC', hash: 'SHA-256' },
    false, ['sign']);
  const mac = await crypto.subtle.sign(
    'HMAC', cryptoKey, encoder.encode(value));
  const hex = Array.from(new Uint8Array(mac))
    .map(byte => byte.toString(16).padStart(2, '0')).join('');
  return KEYED_PREFIX + hex.slice(0, 16);
}

/**
 * Digest `values` under `key` when both a key and a keyed digest are available,
 * and unkeyed otherwise -- one digest per value, in the order given.
 *
 * The unkeyed fallback still keeps plaintext names off the disk, which is worth
 * having on its own; it just doesn't survive an attacker who cares.  Both forms
 * are self-describing, so state written one way is still recognised the other
 * -- a page moved between HTTP and HTTPS reopens on the rows it left open.
 */
export async function digestList(
  key: string | undefined, values: readonly string[]
): Promise<string[]> {
  if (key && canKeyDigests())
    return Promise.all(values.map(value => keyedDigest(key, value)));

  return values.map(fnv1a64);
}

/** digestList as a value -> digest map, for repeated lookups. */
export async function digestAll(
  key: string | undefined, values: readonly string[]
): Promise<ReadonlyMap<string, string>> {
  const digests = await digestList(key, values);
  return new Map(values.map((value, i) => [value, digests[i]]));
}
